import { FC, useState } from 'react'

import {
  Text,
  Badge,
  type BadgeSentiment,
  BorderRadius8,
  Button,
  GreyGrey10,
  GreyGrey60,
  GreyGrey80,
  Primary13Regular,
  Primary13SemiBold,
  Flex
} from '@sequencehq/core-components'
import { format, parseISO } from '@sequencehq/utils/dates'
import type { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'
import { match } from 'ts-pattern'
import { ModalCancelMandate } from './cancelMandate.modal'

type GoCardlessMandateStatus = Dashboardv99990101Api.GoCardlessMandateStatus

const CANCELLABLE_STATUSES = new Set<GoCardlessMandateStatus>([
  'PENDING',
  'PENDING_CUSTOMER_APPROVAL',
  'PENDING_SUBMISSION',
  'CUSTOMER_APPROVAL_GRANTED',
  'CUSTOMER_APPROVAL_SKIPPED',
  'SUBMITTED',
  'ACTIVE',
  'REINSTATED'
])

type GoCardlessMandateProps = {
  mandateId: string
  externalMandateId: string
  status: GoCardlessMandateStatus
  createdAt: string
}

export const GoCardlessMandate: FC<GoCardlessMandateProps> = ({
  mandateId,
  externalMandateId,
  status,
  createdAt
}) => {
  const [showModal, setShowModal] = useState(false)
  const badge = statusBadge(status)

  return (
    <Flex
      flexDir="column"
      gap="12px"
      padding="12px"
      backgroundColor={GreyGrey10}
      borderRadius={BorderRadius8}
      data-testid={`gocardless.mandate.${mandateId}`}
    >
      <Badge
        sentiment={badge.sentiment}
        data-testid={`gocardless.mandate.status.${mandateId}`}
      >
        {badge.label}
      </Badge>
      <Row
        title="Mandate ID"
        value={externalMandateId}
        data-testid={`gocardless.mandate.externalId.${mandateId}`}
      />
      <Row
        title="Created"
        value={format(parseISO(createdAt), 'd MMM yyyy, HH:mm')}
        data-testid={`gocardless.mandate.createdAt.${mandateId}`}
      />

      {CANCELLABLE_STATUSES.has(status) && (
        <>
          <Button
            variant="secondary"
            onClick={() => setShowModal(true)}
            data-testid={`gocardless.mandate.cancel.${mandateId}`}
          >
            Cancel
          </Button>

          {showModal && (
            <ModalCancelMandate
              mandateId={mandateId}
              onClose={() => setShowModal(false)}
            />
          )}
        </>
      )}
    </Flex>
  )
}

const Row: FC<{
  title: string
  value: string
  'data-testid'?: string
}> = ({ title, value, 'data-testid': dataTestId }) => {
  return (
    <Flex flexDir="column" gap="4px">
      <Text {...Primary13SemiBold} color={GreyGrey80}>
        {title}
      </Text>
      <Text {...Primary13Regular} color={GreyGrey60} data-testid={dataTestId}>
        {value}
      </Text>
    </Flex>
  )
}

const statusBadge = (
  status: GoCardlessMandateStatus
): { sentiment: BadgeSentiment; label: string } =>
  match(status)
    .with('PENDING', () => ({
      sentiment: 'highlight' as const,
      label: 'Pending'
    }))
    .with('PENDING_CUSTOMER_APPROVAL', () => ({
      sentiment: 'highlight' as const,
      label: 'Pending approval'
    }))
    .with('PENDING_SUBMISSION', () => ({
      sentiment: 'highlight' as const,
      label: 'Pending submission'
    }))
    .with('CUSTOMER_APPROVAL_GRANTED', () => ({
      sentiment: 'highlight' as const,
      label: 'Approval granted'
    }))
    .with('CUSTOMER_APPROVAL_SKIPPED', () => ({
      sentiment: 'highlight' as const,
      label: 'Approval skipped'
    }))
    .with('SUBMITTED', () => ({
      sentiment: 'info' as const,
      label: 'Submitted'
    }))
    .with('ACTIVE', () => ({ sentiment: 'success' as const, label: 'Active' }))
    .with('REINSTATED', () => ({
      sentiment: 'success' as const,
      label: 'Reinstated'
    }))
    .with('FAILED', () => ({ sentiment: 'error' as const, label: 'Failed' }))
    .with('CANCELLED', () => ({
      sentiment: 'neutral' as const,
      label: 'Cancelled'
    }))
    .with('EXPIRED', () => ({
      sentiment: 'neutral' as const,
      label: 'Expired'
    }))
    .with('CONSUMED', () => ({
      sentiment: 'neutral' as const,
      label: 'Consumed'
    }))
    .with('BLOCKED', () => ({
      sentiment: 'error' as const,
      label: 'Blocked'
    }))
    .with('SUSPENDED_BY_PAYER', () => ({
      sentiment: 'warning' as const,
      label: 'Suspended'
    }))
    .with('UNKNOWN', () => ({
      sentiment: 'neutral' as const,
      label: 'Unknown'
    }))
    .exhaustive()
